import { attributes } from '../../core/ids-attributes';
import { hasClass, getClosestContainerNode, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { escapeHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import type { IdsDataGridColumn } from './ids-data-grid-column';
import IdsDataGridCell from './ids-data-grid-cell';
import '../ids-menu-button/ids-menu-button';
import '../ids-popup-menu/ids-popup-menu';
import '../ids-popup/ids-popup';
import '../ids-menu/ids-menu';
import '../ids-menu/ids-menu-item';
import '../ids-input/ids-input';
import '../ids-dropdown/ids-dropdown';
import '../ids-dropdown/ids-dropdown-list';
import '../ids-icon/ids-icon';
import '../ids-trigger-field/ids-trigger-button';
import '../ids-trigger-field/ids-trigger-field';
import '../ids-date-picker/ids-date-picker-popup';
import '../ids-time-picker/ids-time-picker-popup';

import type IdsDatePickerPopup from '../ids-date-picker/ids-date-picker-popup';
import type IdsMenuItem from '../ids-menu/ids-menu-item';
import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsMenuButton from '../ids-menu-button/ids-menu-button';
import type IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';

export interface IdsDataGridFilterConditions {
  /** The filter element */
  filterElem?: any;
  /** The id of the column */
  columnId?: string;
  /** The value filter */
  value?: string;
  /** The filter operator */
  operator?: 'equals' | 'does-not-equal' | 'contains' | 'does-not-contain' | 'end-with' | 'start-with' | 'does-not-end-with' | 'does-not-start-with' | 'is-empty' | 'is-not-empty' | 'in-range' | 'less-than' | 'less-equals' | 'greater-than' | 'greater-equals' | 'selected' | 'not-selected' | 'selected-notselected' | 'earlier-than' | 'earlier-equals' | 'later-than' | 'later-equals';
}
interface IdsDataGridFilterOperator {
  value: string;
  label: string;
  icon: string;
  selected?: boolean;
}

/**
 * IDS Data Grid Filters
 * @type {object}
 */
export default class IdsDataGridFilters {
  constructor(root: any) {
    this.root = root;
  }

  root: any;

  /**
   * Filter defaults.
   * @type {object}
   */
  DEFAULTS = {
    disableClientFilter: false,
    filterable: true,
    filterWhenTyping: true,
    filterRowDisabled: false
  };

  /**
   * Saved list of conditions to use with filter rerender.
   * @private
   * @type {Array<IdsDataGridFilterConditions>}
   */
  #conditions: any = [];

  /**
   * Initial values to use reset filter.
   * @private
   * @type {object}
   */
  #initial: any = {};

  /**
   * Hold filter in-process state.
   * @private
   * @type {boolean}
   */
  filterIsProcessing = false;

  /**
   * Focused element use with rerender filter.
   * @private
   * @type {HTMLElement|null}
   */
  focused: any;

  /**
   * Suppress filtered event
   * @private
   * @type {boolean}
   */
  #suppressFilteredEvent = false;

  /**
   * Text filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  text(column: IdsDataGridColumn) {
    return this.#btnAndInputTemplate('text', column);
  }

  /**
   * Integer filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  integer(column: IdsDataGridColumn) {
    return this.#btnAndInputTemplate('integer', column);
  }

  /**
   * Decimal filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  decimal(column: IdsDataGridColumn) {
    return this.#btnAndInputTemplate('decimal', column);
  }

  /**
   * Contents filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  contents(column: IdsDataGridColumn) {
    return this.#dropdownTemplate('contents', column);
  }

  /**
   * Dropdown filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  dropdown(column: IdsDataGridColumn) {
    return this.#dropdownTemplate('dropdown', column);
  }

  /**
   * Checkbox filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  checkbox(column: IdsDataGridColumn) {
    return this.#filterButtonTemplate('checkbox', column);
  }

  /**
   * Date filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  date(column: IdsDataGridColumn) {
    const TYPE = 'date';
    const id = `${this.#id(column)}-${TYPE}`;
    const opt = column.filterOptions || {};
    const disabled = opt.disabled ? ' disabled' : '';
    const format = opt.format ? ` format="${opt.format}"` : '';
    const firstDayOfWeek = opt.firstDayOfWeek ? ` first-day-of-week="${opt.firstDayOfWeek}"` : '';
    const showCancel = opt.showCancel ? ` show-cancel="${!!opt.showCancel}"` : '';
    const showClear = opt.showClear ? ` show-clear="${!!opt.showClear}"` : '';
    const showToday = opt.showToday ? ` show-today="${!!opt.showToday}"` : '';
    const showPicklistWeek = opt.showPicklistWeek ? ` show-picklist-week="${!!opt.showPicklistWeek}"` : '';
    const showPicklistMonth = opt.showPicklistMonth ? ` show-picklist-month="${!!opt.showPicklistMonth}"` : '';
    const showPicklistYear = opt.showPicklistYear ? ` show-picklist-year="${!!opt.showPicklistYear}"` : '';
    const showWeekNumbers = opt.showWeekNumbers ? ` show-week-numbers="${!!opt.showWeekNumbers}"` : '';
    const useCurrentTime = opt.useCurrentTime ? ` use-current-time="${!!opt.useCurrentTime}"` : '';
    const useRange = opt.useRange ? ` use-range="${!!opt.useRange}"` : '';

    const filtered = this.#conditions.filter((c: IdsDataGridFilterConditions) => c.columnId === column.id);
    let value = (filtered[0] as any)?.value ?? null;
    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].datePicker) this.#initial[column.id].datePicker = { value };
    value = value ? ` value="${value}"` : '';

    const filterButton = `${this.#filterButtonTemplate(TYPE, column)}`;
    const trigger = `${this.#triggerFieldTemplate(TYPE, column, 'calendar', 'DatePickerTriggerButton')}`;

    const popup = `<ids-date-picker-popup
      attachment=".ids-data-grid-wrapper"
      data-filter-type="${TYPE}"
      trigger-type="click"
      id="popup-${id}"
      no-margins
      compact="true"
      ${format}
      ${firstDayOfWeek}
      ${showToday}${showWeekNumbers}${showClear}${showCancel}
      ${showPicklistWeek}${showPicklistMonth}${showPicklistYear}
      ${useCurrentTime}${useRange}
      ${value}${disabled}></ids-date-picker-popup>`;

    const columnAlign = column.filterAlign ?? column.align;
    if (columnAlign === 'right') return `${trigger}${filterButton}${popup}`;
    return `${filterButton}${trigger}${popup}`;
  }

  /**
   * Time filter markup.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  time(column: IdsDataGridColumn) {
    const TYPE = 'time';
    const id = `${this.#id(column)}-${TYPE}`;
    const opt = column.filterOptions || {};
    const label = opt.label || 'Filter time picker';
    const format = opt.format ? ` format="${opt.format}"` : '';
    const placeholder = opt.placeholder ? ` placeholder="${opt.placeholder}"` : '';
    const minuteInterval = opt.minuteInterval ? ` minute-interval="${opt.minuteInterval}"` : '';
    const secondInterval = opt.secondInterval ? ` second-interval="${opt.secondInterval}"` : '';
    const autoselect = opt.autoselect ? ' autoselect' : '';
    const autoupdate = opt.autoupdate ? ' autoupdate' : '';
    const disabled = opt.disabled ? ' disabled' : '';
    const readonly = opt.readonly ? ' readonly' : '';

    const filtered = this.#conditions.filter((c: IdsDataGridFilterConditions) => c.columnId === column.id);
    let value = (filtered[0] as any)?.value ?? null;
    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].timePicker) this.#initial[column.id].timePicker = { value };
    value = value ? ` value="${value}"` : '';

    const filterButton = `${this.#filterButtonTemplate(TYPE, column)}`;
    const trigger = `${this.#triggerFieldTemplate(TYPE, column, 'clock', 'TimePickerTriggerButton')}`;
    const popup = `<ids-time-picker-popup
        attachment=".ids-data-grid-wrapper"
        trigger-type="click"
        color-variant="${!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'}"
        data-filter-type="${TYPE}"
        size="${opt.size || 'full'}"
        label="${label}"
        label-state="collapsed"
        id="${id}"
        no-margins
        compact
        ${format}${placeholder}${minuteInterval}${secondInterval}
        ${autoselect}${autoupdate}${disabled}${readonly}${value}
      ></ids-time-picker-popup>`;

    if (column?.align === 'right') return `${trigger}${filterButton}${popup}`;
    return `${filterButton}${trigger}${popup}`;
  }

  /**
   * Get filter wrapper element by given column id
   * @param {string} columnId The column id
   * @returns {HTMLElement|undefined} The filter wrapper element
   */
  filterWrapperById(columnId: string | undefined) {
    return this.root.shadowRoot.querySelector(`.ids-data-grid-header-cell[column-id="${columnId}"]
      .ids-data-grid-header-cell-filter-wrapper`);
  }

  /**
   * Returns markup for a header cell filter in data grid.
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The resulting template
   */
  filterTemplate(column: IdsDataGridColumn) {
    const slottedEl = this.root.querySelector(`[slot="filter-${column.id}"]`);
    let html: any = '';
    if (slottedEl) {
      html = `<slot name="filter-${column.id}"></slot>`;
    } else if (typeof column.filterType === 'function') {
      html = column.filterType.apply(this, [column]);
    }
    if (html !== '') {
      let cssClass = 'ids-data-grid-header-cell-filter-wrapper';
      cssClass += !this.root.filterable ? ' hidden' : '';
      cssClass += column.filterOptions?.disabled ? ' disabled' : '';
      html = `<span class="${cssClass}">${html}</span>`;
    }
    return html;
  }

  /**
   * Set datepicker type to range or single date.
   * @private
   * @param {object} datePicker datepicker element.
   * @param {string} operator filter type.
   * @returns {void}
   */
  #setDatePickerValue(datePicker: IdsTriggerField, operator?: string): void {
    if (datePicker) {
      const popupId = datePicker.getAttribute('id')?.replace('field-', 'popup-');
      const popupEl: IdsDatePickerPopup = this.root.wrapper.querySelector(`#${popupId}`)!;
      if (popupEl) {
        if (operator === 'in-range') {
          popupEl.useRange = true;
        } else {
          let startDateValue = datePicker.value;
          if (popupEl.rangeSettings && datePicker.value) {
            startDateValue = datePicker.value.split(popupEl.rangeSettings.separator)[0];
            popupEl.resetRangeSettings();
          }
          popupEl.useRange = false;
          popupEl.value = startDateValue;
        }
      }
    }
  }

  /**
   * Reset all filters as initial state.
   * @returns {void}
   */
  resetFilters() {
    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      if (node) {
        const headerElem = n.closest('.ids-data-grid-header-cell');
        const columnData = this.root.columnDataByHeaderElem(headerElem);
        const initial = this.#initial[columnData.id];

        const input = node.querySelector('ids-input');
        const dropdown = node.querySelector('ids-dropdown');
        const triggerField = node.querySelector('ids-trigger-field');
        const timePicker = node.querySelector('ids-time-picker');
        const btn = node.querySelector('ids-menu-button') as IdsMenuButton | null;

        if (input) input.value = initial?.input?.value || '';
        if (dropdown) dropdown.value = initial?.dropdown?.value || '';
        if (triggerField) triggerField.value = initial?.triggerField?.value || '';
        if (timePicker) timePicker.value = initial?.timePicker?.value || '';
        if (btn) {
          const menuEl: IdsPopupMenu | null = btn.menuEl;
          const initialBtnValue = initial?.btn?.value;
          let item;

          if (menuEl) {
            // Reset filter from popup menu selected states
            item = menuEl.items.filter((itm: any) => itm.value === initialBtnValue)[0] ?? menuEl.items[0];
            menuEl.selectItem(item);
          } else if (columnData.filterType?.name) {
            // If popup menu hasn't been rendered yet, reset filter from column configuration
            const operators = this.#operators(columnData.filterType.name, columnData);
            item = operators.filter((itm: any) => itm.value === initialBtnValue)[0] ?? operators[0];
            if (item) {
              btn.setAttribute(attributes.ICON, item.icon);
              btn.setAttribute(attributes.VALUE, item.value);
              btn.updateBtnText(item.label);
            }
          }

          if (triggerField && item) this.#setDatePickerValue(triggerField, item.value);
        }
      }
    });
  }

  /**
   * Set filter conditions on the UI Only.
   * @param {Array<IdsDataGridFilterConditions>} conditions An array of objects with the filter conditions.
   * @returns {Array<object>} An array of currently showing filter conditions.
   */
  setFilterConditions(conditions: Array<IdsDataGridFilterConditions>) {
    this.#suppressFilteredEvent = true;
    this.resetFilters();
    const toBeRemoved: any = [];
    conditions.forEach((condition, i) => {
      const headerFilterWrapper = this.filterWrapperById(condition.columnId);
      if (!headerFilterWrapper || hasClass(headerFilterWrapper, 'disabled') || hasClass(headerFilterWrapper, 'readonly')) return;

      const headerSlot = headerFilterWrapper.querySelector('slot[name^="filter-"]');
      const headerRoot = headerSlot ? headerSlot.assignedElements()[0] : headerFilterWrapper;
      if (headerRoot) {
        const input = headerRoot.querySelector('ids-input');
        const button = headerRoot.querySelector('ids-menu-button') as IdsMenuButton | null;
        const dropdown = headerRoot.querySelector('ids-dropdown');
        const triggerField = headerRoot.querySelector('ids-trigger-field');
        const timePicker = headerRoot.querySelector('ids-time-picker');
        const headerCell = headerRoot.closest('.ids-data-grid-header-cell');
        const columnData = this.root.columnDataByHeaderElem(headerCell);

        const conditionValue = condition.value || '';
        if (input) input.value = conditionValue;
        if (triggerField) triggerField.value = conditionValue;
        if (timePicker) timePicker.value = conditionValue;
        if (dropdown) {
          dropdown.value = conditionValue;
          if (dropdown.value === this.#dropdownNotFilterItem(columnData).value) toBeRemoved.push(i);
        }
        if (timePicker && !columnData.formatOptions?.dateFormat && !columnData.formatOptions?.timeStyle) {
          if (!columnData.formatOptions) columnData.formatOptions = {};
          columnData.formatOptions.dateFormat = timePicker.format;
          columnData.formatOptions.timeStyle = 'short';
        }
        if (button) {
          const menuEl = button.menuEl;
          const conditionOperator = condition.operator;

          if (menuEl) {
            // If Popup Menu rendered, get selected values from it
            const item = menuEl.items.filter((itm: any) => itm.value === conditionOperator)[0] ?? menuEl.items[0];
            if (item) button.menuEl.selectItem(item);
          } else if (columnData.filterType?.name) {
            // If Popup Menu not rendered, get operator from column data and update menu button
            const operators = this.#operators(columnData.filterType.name, columnData);
            const item = operators.filter((itm: any) => itm.value === conditionOperator)[0] ?? operators[0];
            if (item) {
              button.setAttribute(attributes.ICON, item.icon);
              button.setAttribute(attributes.VALUE, item.value);
              button.updateBtnText(item.label);
            }
          }
        }
        if (!condition.filterElem) condition.filterElem = timePicker || triggerField || dropdown || input;
        this.#setDatePickerValue(triggerField, condition.operator);
      }
    });
    if (toBeRemoved.length) conditions = conditions.filter((condition, idx) => !toBeRemoved.includes(idx));
    this.#suppressFilteredEvent = false;
    return conditions;
  }

  /**
   * Get filter conditions in array from whats set in the UI.
   * @returns {Array<object>} An array of currently showing filter conditions.
   */
  filterConditions(): Array<IdsDataGridFilterConditions> {
    const filterExpr: any = [];
    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      if (node) {
        const input = node.querySelector('ids-input');
        const triggerField = node.querySelector('ids-trigger-field');
        const btn = node.querySelector('ids-menu-button');
        const dropdown = node.querySelector('ids-dropdown');
        const timePicker = node.querySelector('ids-time-picker');
        if (!btn && !input && !triggerField && !dropdown) return;

        const headerElem = n.closest('.ids-data-grid-header-cell');
        const columnData = this.root.columnDataByHeaderElem(headerElem);
        let operator = btn?.value?.[0]
          || btn?.menuEl?.items?.[0]?.value
          || this.#operators(columnData.filterType?.name, columnData)?.[0]?.value;
        if (!operator && !btn && input) operator = 'contains';
        const value = input?.value ?? triggerField?.value ?? dropdown?.value ?? timePicker?.value ?? '';
        if (dropdown) {
          operator = 'equals';
          if (value === this.#dropdownNotFilterItem(columnData).value) return;
        }
        if (timePicker && !columnData.formatOptions?.dateFormat && !columnData.formatOptions?.timeStyle) {
          if (!columnData.formatOptions) columnData.formatOptions = {};
          columnData.formatOptions.dateFormat = timePicker.format;
          columnData.formatOptions.timeStyle = 'short';
        }
        this.#setDatePickerValue(triggerField, operator);

        if (operator === 'selected-notselected') return;
        if (value === '' && !dropdown && !(/\b(is-not-empty|is-empty|selected|not-selected)\b/g.test(operator))) return;

        const filterElem = timePicker || dropdown || triggerField || input;

        const condition = {
          columnId: columnData.id,
          columnData,
          filterElem,
          operator,
          value
        };

        filterExpr.push(condition);
      }
    });

    return filterExpr;
  }

  /**
   * Apply the Filter with the currently selected conditions, or the ones passed in.
   * @param {Array} conditions An array of objects with the filter conditions
   * @returns {void}
   */
  applyFilter(conditions?: any) {
    this.root?.resetCache();

    if (this.filterIsProcessing || !this.root.filterable || this.root.filterRowDisabled) return;
    this.filterIsProcessing = true;

    if (conditions) {
      conditions = this.setFilterConditions(conditions);
    } else {
      conditions = this.filterConditions();
    }
    this.#conditions = conditions;

    // Client filter
    if (this.root.disableClientFilter) {
      this.root.triggerEvent('filtered', this.root, { bubbles: true, detail: { elem: this.root, conditions } });
      this.filterIsProcessing = false;
      this.root.saveSettings?.();
      return;
    }

    // No need to go further, if no condition/s to filter
    if ((!conditions?.length && !this.root.datasource.filtered) || !conditions) {
      this.filterIsProcessing = false;
      return;
    }

    // Should fire clear event
    const isCleared = !conditions.length && this.root.datasource.filtered;
    let isFilterApply = false;

    const checkColumn = (c: any, row: any, index: any, isKeyword: boolean = false) => {
      let isMatch = true;
      if (!c.columnData) {
        c.columnData = this.root.columnDataById(c.columnId);
        if (!c.columnData) return;
      }

      if (!isKeyword) {
        // Must be filter type
        if (typeof c.columnData.filterType !== 'function'
          && typeof c.columnData.filterFunction !== 'function') return;

        // Use custom filter
        if (typeof c.columnData.filterFunction === 'function') {
          isMatch = c.columnData.filterFunction({ data: row, index, condition: c });
          isFilterApply = true; // Add this line to indicate filter was applied
          if (!isMatch) {
            this.filterIsProcessing = false;
          }
          return isMatch; // Return the match result directly
        }
      }

      // All other use in-built filters
      const filterType = c.columnData.filterType?.name || 'text';

      // Single filter
      const filterWrapper = this.filterWrapperById(c.columnId);
      if (hasClass(filterWrapper, 'disabled') || hasClass(filterWrapper, 'readonly')) return;

      const column = c.columnData;

      const stringVal = (v: any) => ((v === null || v === undefined) ? '' : v.toString().toLowerCase());
      const formatterVal = () => {
        const val = IdsDataGridCell.template(row, column, index, this.root);
        const rex = /(<([^>]+)>)|(amp;)|(&lt;([^>]+)&gt;)/ig;
        return val.replace(rex, '').trim().toLowerCase();
      };
      let conditionValue = stringVal(c.value);
      let value = row[column.field];
      let valueStr = stringVal(value);

      // Dropdown, multiselect and contents types
      if (/dropdown|multiselect|contents/g.test(filterType)) {
        if ((value === null || typeof value === 'undefined') && c.value === '') {
          const temp = 'filter-blank-value-temp';
          value = temp;
          conditionValue = temp;
        } else {
          conditionValue = c.value;
        }
      }

      // Run Data over the formatter
      if (filterType === 'text') {
        value = formatterVal();
        valueStr = stringVal(value);
      }

      // Integer
      if (filterType === 'integer') {
        value = formatterVal();
        value = this.root.localeAPI?.parseNumber(value);
        conditionValue = this.root.localeAPI?.parseNumber(conditionValue);
      }

      // Decimal
      if (filterType === 'decimal') {
        value = formatterVal();
        value = this.root.localeAPI?.parseNumber(value);
        conditionValue = this.root.localeAPI?.parseNumber(conditionValue);
      }

      // Date Time
      if (filterType === 'date' || filterType === 'time') {
        if (/string|undefined/g.test(typeof value)) value = formatterVal();
        const getValues = (rValue: any, cValue: any) => {
          // NOTE: HERE IS THE DATE PROBLEM, format is always equal to a time format, not a date format
          const defaultFilterFormat = filterType === 'date'
            ? this.root.localeAPI?.calendar().dateFormat
            : this.root.localeAPI?.calendar().timeFormat;
          const format = c.format || column.formatOptions || defaultFilterFormat;

          cValue = this.root.localeAPI?.parseDate(cValue, format);
          if (cValue) {
            if (filterType === 'time') {
              // drop the day, month and year
              cValue.setDate(1);
              cValue.setMonth(0);
              cValue.setYear(0);
            }

            cValue = cValue.getTime();
          }

          if (typeof rValue === 'string' && rValue) {
            rValue = this.root.localeAPI?.parseDate(rValue, format);

            if (rValue) {
              if (filterType === 'time') {
                // drop the day, month and year
                rValue.setDate(1);
                rValue.setMonth(0);
                rValue.setYear(0);
              } else if (!(column.formatOptions?.showTime)) {
                // Drop any time component of the row data for the filter
                // as it is a date only field
                rValue.setHours(0);
                rValue.setMinutes(0);
                rValue.setSeconds(0);
                rValue.setMilliseconds(0);
              }
              rValue = rValue.getTime();
            }
          }
          return { rValue, cValue };
        };

        let values = null;
        if (c.operator === 'in-range') { // range stuff
          const filterButton = c.filterElem.querySelector('ids-trigger-button');
          const datePickerPopupSelector = c.filterElem.getAttribute('aria-controls') ?? filterButton?.getAttribute('aria-controls');
          const datePickerPopup: IdsDatePickerPopup = this.root.wrapper.querySelector(`#${datePickerPopupSelector}`)!;

          if (datePickerPopup) {
            if (c.value.indexOf(datePickerPopup.rangeSettings.separator) > -1) {
              const cValues = c.value.split(datePickerPopup.rangeSettings.separator);
              const range = { start: getValues(value, cValues[0]), end: getValues(value, cValues[1]) };
              values = {
                rValue: range.start.rValue,
                cValue: { start: range.start.cValue, end: range.end.cValue }
              };
            } else values = getValues(value, c.value);
          }
        } else {
          values = getValues(value, c.value);
        }
        value = values ? values.rValue : value;
        conditionValue = values ? values.cValue : conditionValue;
      }
      switch (c.operator) {
        case 'equals':
          isMatch = (value === conditionValue && value !== '');
          break;
        case 'does-not-equal':
          isMatch = (value !== conditionValue);
          break;
        case 'contains':
          isMatch = (valueStr.indexOf(conditionValue) > -1 && value.toString() !== '');
          break;
        case 'does-not-contain':
          isMatch = (valueStr.indexOf(conditionValue) === -1);
          break;
        case 'end-with':
          // amazonq-ignore-next-line
          isMatch = (valueStr.lastIndexOf(conditionValue) === (valueStr.length - conditionValue.toString().length) && valueStr !== '' && (valueStr.length >= conditionValue.toString().length));
          break;
        case 'start-with':
          isMatch = (valueStr.indexOf(conditionValue) === 0 && valueStr !== '');
          break;
        case 'does-not-end-with':
          // amazonq-ignore-next-line
          isMatch = (valueStr.lastIndexOf(conditionValue) === (valueStr.length - conditionValue.toString().length) && valueStr !== '' && (valueStr.length >= conditionValue.toString().length));
          isMatch = !isMatch;
          break;
        case 'does-not-start-with':
          isMatch = !(valueStr.indexOf(conditionValue) === 0 && valueStr !== '');
          break;
        case 'is-empty':
          isMatch = (valueStr === '');
          break;
        case 'is-not-empty':
          if (typeof value === 'string') {
            isMatch = !!value.length;
            break;
          }
          if (typeof value === 'number') {
            isMatch = !Number.isNaN(value);
            break;
          }
          isMatch = value !== null && value !== undefined;
          break;
        case 'in-range':
          if (typeof conditionValue === 'object') {
            isMatch = value >= conditionValue.start && value <= conditionValue.end && value !== '';
          } else isMatch = (value === conditionValue && value !== '');
          break;
        case 'less-than':
          isMatch = (value < conditionValue && (value !== '' && value !== null));
          break;
        case 'earlier-than':
          isMatch = (value < conditionValue && (value !== '' && value !== null));
          break;
        case 'less-equals':
          isMatch = (value <= conditionValue && (value !== '' && value !== null));
          break;
        case 'earlier-equals':
          isMatch = (value <= conditionValue && (value !== '' && value !== null));
          break;
        case 'greater-than':
          isMatch = (value > conditionValue && (value !== '' && value !== null));
          break;
        case 'greater-equals':
          isMatch = (value >= conditionValue && (value !== '' && value !== null));
          break;
        case 'later-than':
          isMatch = (value > conditionValue && (value !== '' && value !== null));
          break;
        case 'later-equals':
          isMatch = (value >= conditionValue && (value !== '' && value !== null));
          break;
        case 'selected':
          if (typeof c.columnData.isChecked === 'function') {
            isMatch = c.columnData.isChecked(value);
            break;
          }
          isMatch = (valueStr === '1' || valueStr === 'true' || value === true || value === 1) && valueStr !== '';
          break;
        case 'not-selected':
          if (typeof c.columnData.isChecked === 'function') {
            isMatch = !c.columnData.isChecked(value);
            break;
          }
          isMatch = (valueStr === '0' || valueStr === 'false' || value === false || value === 0 || valueStr === '');
          break;
        case 'selected-notselected':
          isMatch = true;
          break;
        default:
          break;
      }

      isFilterApply = true;

      if (!isMatch) {
        this.filterIsProcessing = false;
      }

      return isMatch;
    };

    const checkRow = (row: any, index: number) => {
      let isMatch = true;

      for (let i = 0, l = conditions.length; i < l; i++) {
        const c = conditions[i] || {};

        if (c?.column === 'all') {
          const columns = this.root.columns;
          for (let j = 0; j < columns.length; j++) {
            const column = columns[j];
            const columnMatch = checkColumn({ ...c, columnId: column?.id }, row, index, true);
            isMatch = !!columnMatch;
            if (isMatch) {
              break;
            }
          }
        } else {
          const columnMatch = checkColumn(c, row, index);
          if (typeof columnMatch === 'undefined') {
            continue;
          }
          if (!columnMatch) {
            isMatch = false;
            break;
          }
        }
      }
      this.filterIsProcessing = false;
      return !isMatch;
    };

    this.root.datasource.filter(checkRow);
    this.root.redrawBody();

    // Fires after filter action occurs
    if (isCleared || isFilterApply) {
      this.root.toggleEmptyMessage();
      this.root.triggerEvent('filtered', this.root, {
        bubbles: true,
        detail: { elem: this.root, type: isCleared ? 'clear' : 'apply', conditions }
      });
      this.root.saveSettings?.();
    }

    requestAnimationFrame(() => {
      this.root.setActiveCell(0, 0, true);
    });
  }

  /**
   * Set disabled filter row
   * @returns {void}
   */
  setFilterRowDisabled() {
    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      if (node) {
        [
          node.querySelector('ids-input'),
          node.querySelector('ids-menu-button'),
          node.querySelector('ids-dropdown'),
          node.querySelector('ids-date-picker'),
          node.querySelector('ids-trigger-field'),
          node.querySelector('ids-time-picker')
        ].forEach((el) => {
          if (this.root.filterRowDisabled) {
            n.classList.add('disabled');
            el?.setAttribute('disabled', '');
          } else {
            n.classList.remove('disabled');
            el?.removeAttribute('disabled');
          }
        });
      }
    });
  }

  /**
   * Attach filters setting after data grid render
   * @returns {void}
   */
  attachFilterSettings() {
    // Set filter row should be disabled or not
    if (this.root.filterRowDisabled) this.setFilterRowDisabled();

    // Set compulsory attributes for slotted elements
    const setCompulsoryAttributes = (el: any) => {
      el?.setAttribute('color-variant', (!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'));
      el?.setAttribute('label-state', 'collapsed');
      el?.setAttribute('no-margins', '');
      el?.setAttribute('compact', '');
    };

    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      const headerElem = n.closest('.ids-data-grid-header-cell');
      const column = this.root.columnDataByHeaderElem(headerElem);
      const input = node?.querySelector('ids-input');
      const type = input?.getAttribute('data-filter-type');
      const dropdown = node?.querySelector('ids-dropdown');
      const dropdownList = node?.querySelector('ids-dropdown-list');
      const datePicker = node?.querySelector('ids-date-picker');
      const timePicker = node?.querySelector('ids-time-picker');
      const btn = node?.querySelector('ids-menu-button');
      const menu = node?.querySelector('ids-popup-menu');
      let triggerBtn = node?.querySelector('ids-trigger-button');
      let triggerField = node?.querySelector('ids-trigger-field');
      const datePickerPopup = node?.querySelector('ids-date-picker-popup');
      const timePickerPopup = node?.querySelector('ids-time-picker-popup');
      let menuAttachment = '.ids-data-grid-wrapper';

      // Slotted filter only
      if (slot && (input || dropdown || datePicker || timePicker || btn)) {
        menuAttachment = 'ids-data-grid';

        // Slotted initial
        this.#initial[column.id] = this.#initial[column.id] || {};
        const initial = this.#initial[column.id];
        if (input && !initial.input) initial.input = { value: input.value };
        if (dropdown && !initial.dropdown) initial.dropdown = { value: dropdown.value };
        if (datePicker && !initial.datePicker) initial.datePicker = { value: datePicker.value };
        if (timePicker && !initial.timePicker) initial.timePicker = { value: timePicker.value };
        if (btn && !initial.btn) initial.btn = { value: btn.value?.[0] };

        // Slotted attributes
        setCompulsoryAttributes(input);
        setCompulsoryAttributes(dropdown);
        setCompulsoryAttributes(datePicker);
        setCompulsoryAttributes(timePicker);
        if (btn) {
          btn.cssClass = [...new Set([...btn.cssClass, 'compact'])];
          btn.setAttribute('color-variant', (!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'));
          btn.setAttribute('column-id', column.id);
          btn.setAttribute('square', 'true');
        }
        // Place slotted menus into a special slot placed near internal filter menus
        if (menu) {
          menu.setAttribute('slot', `menu-container`);
        }
      }

      // Connect Popup Menus
      if (menu) {
        if (slot) {
          triggerField = slot.assignedElements()[0].querySelector('ids-input');
          triggerBtn = slot.assignedElements()[0].querySelector('ids-menu-button');
          menu.setAttribute(attributes.TARGET, `#${triggerBtn.getAttribute('id')}`);
          menu.setAttribute(attributes.TRIGGER_ELEM, `#${triggerBtn.getAttribute('id')}`);
        } else {
          menu.setAttribute(attributes.ATTACHMENT, menuAttachment);
        }
        menu.setAttribute(attributes.TRIGGER_TYPE, 'click');
        menu.removeTriggerEvents();

        // Move/Rebind menu (order of these statements matters)
        menu.appendToTargetParent();
        menu.keyboardEventTarget = this.root;
        menu.attachEventHandlers();
        menu.attachKeyboardListeners();
        if (menu.popup) {
          menu.popup.popupOpenEventsTarget = document.body;
          const isInModal = getClosest(menu, 'ids-modal') !== undefined;
          menu.popup.positionStyle = isInModal ? 'fixed' : 'absolute';
          menu.popup.onPlace = (popupRect: DOMRect) => {
            if (isInModal) popupRect.y += 58;
            return popupRect;
          };
          menu.popup.onOutsideClick = () => {
            menu.hide();
          };
        }
      }

      btn?.setAttribute('data-filter-conditions-button', '');

      const dateOrTimePopup = datePickerPopup || timePickerPopup;
      if (dateOrTimePopup) {
        dateOrTimePopup.appendToTargetParent();
        if (dateOrTimePopup.popup) {
          dateOrTimePopup.popup.popupOpenEventsTarget = document.body;
          dateOrTimePopup.popup.onOutsideClick = (e: MouseEvent) => {
            if (!e.composedPath().includes(dateOrTimePopup)) { dateOrTimePopup.hide(); }
          };
        }
        dateOrTimePopup.setAttribute(attributes.TRIGGER_TYPE, 'click');
        dateOrTimePopup.setAttribute(attributes.TARGET, `#${triggerField.getAttribute(attributes.ID)}`);
        dateOrTimePopup.setAttribute(attributes.ALIGN, 'bottom, right');

        dateOrTimePopup.setAttribute(attributes.TRIGGER_ELEM, `#${triggerBtn.getAttribute(attributes.ID)}`);
        dateOrTimePopup.popup?.setAttribute(attributes.ARROW_TARGET, `#${triggerBtn.getAttribute(attributes.ID)}`);
        if (dateOrTimePopup.popup) dateOrTimePopup.popup.y = 16;
        dateOrTimePopup.setAttribute(attributes.ATTACHMENT, menuAttachment);
        dateOrTimePopup.refreshTriggerEvents();
      }

      // Date Picker - Adjust range settings
      if (datePickerPopup) {
        const rangeSettings = column.filterOptions?.rangeSettings;
        if (rangeSettings) datePickerPopup.rangeSettings = rangeSettings;
        datePickerPopup.useRange = (btn?.value?.[0] === 'in-range');
      }

      // Time Picker - Adjust format
      if (timePickerPopup) {
        const format = this.root.localeAPI?.calendar().timeFormat;
        triggerField.format = format;
        timePickerPopup.format = format;
      }

      // Dropdown/List settings
      if (dropdown && dropdownList) {
        dropdownList.configurePopup();
        dropdownList.setAttribute('size', 'full');
        dropdown.input.value = (dropdownList.selected || dropdownList.listBox?.children[0])?.textContent || '';
        dropdownList.setAttribute(attributes.ATTACHMENT, menuAttachment);
        dropdownList.appendToTargetParent();
        if (dropdownList.popup) {
          dropdownList.popup.popupOpenEventsTarget = document.body;
        }
        dropdownList.setAttribute(attributes.TRIGGER_TYPE, 'custom');
        dropdownList.setAttribute(attributes.TARGET, `#${dropdown.getAttribute('id')}`);
        dropdownList.setAttribute(attributes.TRIGGER_ELEM, `#${dropdown.getAttribute('id')}`);
        dropdown.onEvent('click', dropdown, () => {
          const popup = dropdownList.popup;
          if (popup) {
            if (!popup.visible) dropdown.open();
            else dropdown.close();
          }
        });
        dropdownList.onOutsideClick = (e: MouseEvent) => {
          if (!e.composedPath().includes(dropdownList)) {
            dropdown.close();
          }
        };
        dropdownList.refreshTriggerEvents();
      }

      // Integer type mask
      if (type === 'integer') {
        input.mask = 'number';
        input.maskOptions = {
          allowDecimal: false,
          allowNegative: true,
          allowLeadingZeros: true,
          allowThousandsSeparator: false
        };
      }

      // Decimal type mask
      if (type === 'decimal') {
        input.mask = 'number';
        input.maskOptions = {
          allowDecimal: true,
          allowLeadingZeros: true,
          allowNegative: true
        };
      }
    });
  }

  /**
   * Handle all triggering and handling of events
   * @returns {void}
   */
  attachFilterEventHandlers() {
    if (!this.root.filterable || !this.root.header) return;

    this.root.offEvent(`keydown.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`keydown.${this.#id()}`, this.root.wrapper, (e: any) => {
      const elem = e.target;
      if (!elem) return;

      // Only apply this event to handlers that exist on internal (not slotted) filter menus
      const containerNode = getClosestContainerNode(elem);
      if (containerNode === document) return;

      const key = e.key;
      if (key === 'ArrowDown') {
        if (/ids-trigger-field/gi.test(elem.nodeName)) {
          elem.querySelector('ids-trigger-button').click();
        }
        if (/ids-dropdown/gi.test(elem.nodeName)) {
          elem.click();
        }
      }

      this.root.lastActiveCell = null;
    });

    this.root.offEvent(`hide.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`hide.${this.#id()}`, this.root.wrapper, (e: any) => {
      const elem = e.target;
      if (!elem) return;

      // Only apply this event to handlers that exist on internal (not slotted) filter menus
      const containerNode = getClosestContainerNode(elem);
      if (containerNode === document) return;

      if (this.root.openMenu) {
        this.root.offEvent(`keydown.${this.#id()}`, this.root.openMenu);
        this.root.openMenu = null;
      }

      // Popup Menus/Dropdown Lists
      if (/ids-popup-menu|ids-dropdown-list/gi.test(elem.nodeName)) {
        this.root.offEvent(`keydown.${this.#id()}`, elem);
      }

      // Anything extending IdsPickerPopup
      if (/ids-time-picker-popup|ids-date-picker-popup/gi.test(elem.nodeName)) {
        if (!this.root.contains(document.activeElement)) return;
        if (!this.root.shadowRoot.contains(document.activeElement)) return;

        elem.target?.focus();
      }
    });

    this.root.offEvent(`show.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`show.${this.#id()}`, this.root.wrapper, (e: any) => {
      const elem = e.target;
      if (!elem) return;

      // Only apply this event to handlers that exist on internal (not slotted) filter menus
      const containerNode = getClosestContainerNode(elem);
      if (containerNode === document) return;

      requestAnimationFrame(() => {
        this.root.openMenu = elem;
      });

      // Popup Menus
      if (/ids-popup-menu/gi.test(elem.nodeName)) {
        this.root.offEvent(`keydown.${this.#id()}`, elem);
        this.root.onEvent(`keydown.${this.#id()}`, elem, (f: any) => {
          const key = f.key;
          if (['Enter', 'SpaceBar', ' '].includes(key)) {
            elem.selectItem(elem.lastNavigated);
            elem.hideAndFocus(true);
          }
          if (key === 'Escape') {
            elem.hideAndFocus(true);
          }
        });
      }

      // Dropdown Lists
      if (/ids-dropdown-list/gi.test(elem.nodeName)) {
        this.root.offEvent(`keydown.${this.#id()}`, elem);
        this.root.onEvent(`keydown.${this.#id()}`, elem, (f: any) => {
          const key = f.key;
          if (key === 'ArrowUp') {
            elem.navigate(-1);
          }
          if (key === 'ArrowDown') {
            elem.navigate(1);
          }
          if (['Enter', 'SpaceBar', ' '].includes(key)) {
            this.root.selectWithFilterDropdown = true;
            elem.select();
          }
          if (key === 'Escape') {
            if (elem.nodeName === 'IDS-DROPDOWN-LIST') {
              elem.popup.visible = false;
            } else if (elem.triggerCloseEvent) elem?.triggerCloseEvent(true);
          }
        });
      }
    });

    // Captures selected contents from menu/dropdown list items.
    this.root.offEvent(`selected.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`selected.${this.#id()}`, this.root.wrapper, (e: any) => {
      const elem = e.detail?.elem;
      if (!elem) return;

      if (/ids-menu-item/gi.test(elem.nodeName)) {
        this.#handleMenuButtonSelected(elem);
      }

      if (/ids-dropdown-list/gi.test(elem.nodeName)) {
        e.target.value = e.detail.value;
      }
    });

    // Change event for input, dropdown and multiselect
    const header = this.root.container?.querySelector('.ids-data-grid-header:not(.column-groups)');
    this.root.offEvent(`input.${this.#id()}`, header);
    this.root.onEvent(`input.${this.#id()}`, header, (e: any) => {
      const nodeName = e.target?.nodeName;
      if (!this.#suppressFilteredEvent && nodeName && /ids-(input|trigger-field|dropdown|multiselect)/gi.test(nodeName)) {
        this.applyFilter();
      }
    });

    // Capture 'dayselected' events from IdsDatePickerPopup
    this.root.offEvent(`dayselected.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`dayselected.${this.#id()}`, this.root.wrapper, (e: any) => {
      // this will trigger the above change event which also calls applyFilter().
      e.target.value = e.detail.value;
      if (!this.#suppressFilteredEvent) {
        this.applyFilter();
      }
    });

    // Capture 'timeselected' events from IdsTimePickerPopup
    this.root.offEvent(`timeselected.${this.#id()}`, this.root.wrapper);
    this.root.onEvent(`timeselected.${this.#id()}`, this.root.wrapper, (e: any) => {
      // this will trigger the above change event which also calls applyFilter().
      e.target.value = e.detail.value;
      if (!this.#suppressFilteredEvent) {
        this.applyFilter();
      }
    });

    // Change event for ids-date-picker and ids-time-picker
    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      const elem = node?.querySelector('ids-date-picker, ids-time-picker');
      if (elem) {
        this.root.offEvent(`change.${this.#id()}`, elem.input);
        this.root.onEvent(`change.${this.#id()}`, elem.input, () => this.applyFilter());
      }
    });

    // Set filter event when typing for input
    this.setFilterWhenTyping();
  }

  /**
   * Handles `selected` events from filter menus
   * @param {IdsMenuItem} el reference to the menu item that triggered the event
   * @returns {void}
   */
  #handleMenuButtonSelected(el: IdsMenuItem) {
    const target = (el.menu as any)?.target;
    if (!target?.hasAttribute('data-filter-conditions-button')) return;

    const { value, icon, text: label } = el;
    if (!icon || !target || target.icon === icon.replace(/^filter-/g, '')) return;

    const columnId = target.getAttribute('column-id');
    const initial = this.#initial[columnId].btn;
    const beforeChange = {
      icon: target.icon,
      value: `${target.icon}`.replace(/^filter-/g, ''),
      label: (target.text || '').trim()
    };
    target.icon = icon;
    target.querySelector('span, ids-text').textContent = label;
    this.root.triggerEvent('filteroperatorchanged', this.root, {
      bubbles: true,
      detail: {
        elem: this.root,
        targetElem: target,
        icon,
        value,
        label,
        initial,
        beforeChange
      }
    });
    this.applyFilter();
  }

  /**
   * Set filter event when typing for all filter inputs
   * @returns {void}
   */
  setFilterWhenTyping() {
    this.filterNodes?.forEach((n: any) => {
      const slot = n.querySelector('slot[name^="filter-"]');
      const node = slot ? slot.assignedElements()[0] : n;
      const input = node?.querySelector('ids-input');
      if (!input) return;
      const eventName = `keydownend.${input.id}`;
      if (this.root.filterWhenTyping) {
        n.classList.add('filter-when-typing');
        this.root.onEvent(eventName, input, () => {
          const canFilter = input.value?.length > 3 || input.value?.length === 0;
          this.focused = input;
          if (canFilter) this.applyFilter();
        });
      } else {
        n.classList.remove('filter-when-typing');
        this.root.offEvent(eventName, input);
      }
    });
  }

  /**
   * Returns the markup for filter menu button and input.
   * @private
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info.
   * @returns {string} The resulting template
   */
  #btnAndInputTemplate(type: string, column: IdsDataGridColumn) {
    const input = `${this.#inputTemplate(type, column)}`;
    const button = `${this.#filterButtonTemplate(type, column)}`;
    const align = column.filterAlign || column.align;
    if (align === 'right') {
      return `${input}${button}`;
    }
    return `${button}${input}`;
  }

  /**
   * Returns the markup for filter menu button in header cell.
   * @private
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info.
   * @returns {string} The resulting template
   */
  #filterButtonTemplate(type: string, column: IdsDataGridColumn): string {
    const operators = this.#operators(type, column);

    if (!operators?.length) return '';

    const opt = column.filterOptions || {};
    const disabled = opt.disabled ? ' disabled' : '';
    const readonly = opt.readonly ? ' readonly' : '';
    const id = `${this.#id(column)}-${type}`;
    let sel: IdsDataGridFilterOperator = operators.find((op) => op.selected)
      || operators[0]
      || { value: '', icon: '', label: '' };

    if (this.#conditions.length) {
      const condition = this.#conditions.find((c: any) => c.columnId === column.id);
      sel = operators.find((op) => (op.value === condition?.operator)) ?? sel;
    }

    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].btn) this.#initial[column.id].btn = { ...sel };

    return `<ids-menu-button
      color-variant="${!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'}"
      css-class="compact"
      icon="${sel.icon}"
      column-id="${column.id}"
      id="btn-${id}"
      ref-id="${id}"
      menu="menu-${id}"
      value="${sel.value}"
      square="true"
      trigger-type="click"
      ${disabled}${readonly}
      dropdown-icon>
      <span class="audible">${sel?.label}</span>
    </ids-menu-button>`;
  }

  /**
   * Returns filter IdsPopupMenu template for header cell
   * @param {string} id ID of btn and popup pairing
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info.
   * @returns {string} The resulting template
   */
  filterButtonPopupTemplate(id: string, type: string, column: IdsDataGridColumn): string {
    const operators = this.#operators(type, column);

    if (!operators?.length) return '';

    let sel: IdsDataGridFilterOperator = operators.find((op) => op.selected)
      || operators[0]
      || { value: '', icon: '', label: '' };

    if (this.#conditions.length) {
      const condition = this.#conditions.find((c: any) => c.columnId === column.id);
      sel = operators.find((op) => (op.value === condition?.operator)) ?? sel;
    }

    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].btn) this.#initial[column.id].btn = { ...sel };

    const items: string = operators.map((op, i) => {
      const selected = op.value === sel?.value ? ' selected="true"' : '';
      return `<ids-menu-item id="item-${i}-${id}" value="${op.value}" icon="${op.icon}"${selected}>${op.label}</ids-menu-item>`;
    }).join('');

    return `<ids-popup-menu id="menu-${id}" target="#btn-${id}" trigger-type="click">
      <ids-menu-group select="single">${items}</ids-menu-group>
    </ids-popup-menu>`;
  }

  /**
   * Get input template string
   * @private
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info.
   * @returns {string} The template string
   */
  #inputTemplate(type: any, column: any) {
    const opt = column.filterOptions || {};
    const id = typeof opt?.customId === 'function'
      ? opt?.customId(column)
      : opt?.customId || `${this.#id(column)}-${type}`;
    const label = opt.label || 'Filter Input';
    const placeholder = opt.placeholder ? ` placeholder="${opt.placeholder}"` : '';
    const disabled = opt.disabled ? ' disabled' : '';
    const readonly = opt.readonly ? ' readonly' : '';
    const maxlength = opt.maxlength ? ` maxlength="${opt.maxlength}"` : '';
    const uppercase = opt.uppercase ? ` uppercase` : '';

    let value = this.#conditions.filter((c: any) => c.columnId === column.id)[0]?.value ?? null;
    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].input) this.#initial[column.id].input = { value };
    value = value ? ` value="${value}"` : '';

    return `<ids-input
      color-variant="${!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'}"
      data-filter-type="${type}"
      type="${opt.type || 'text'}"
      size="${opt.size || 'full'}"
      label="${label}"
      label-state="collapsed"
      ${this.root.rowHeight === 'xxs' || this.root.rowHeight === 'xxs' ? `field-height="xs"` : ''}
      id="${id}"
      text-align="${column.align === 'right' ? 'end' : 'start'}"
      no-margins
      compact
      ${opt.browserAutocomplete ? ` browser-autocomplete="${opt.browserAutocomplete}"` : ` autocomplete="off"`}
      ${placeholder}${disabled}${readonly}${maxlength}${uppercase}${value}>
    </ids-input>`;
  }

  /**
   * Get trigger field template string
   * @private
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info.
   * @param {string} [icon] if provided, defines the icon used on this field's trigger button
   * @param {string} [btnText] if provided, defines the text content of this field's trigger button
   * @returns {string} The template string
   */
  #triggerFieldTemplate(type: any, column: any, icon?: string, btnText?: string) {
    const opt = column.filterOptions || {};
    const id = typeof opt?.customId === 'function'
      ? opt?.customId(column)
      : opt?.customId || `field-${this.#id(column)}-${type}`;
    const triggerBtnId = `triggerBtn-${this.#id(column)}-${type}`;
    const triggerBtnIcon = icon ? ` icon="${icon}"` : '';
    const triggerBtnText = btnText ? `<ids-text audible="true" translate-text="true">${btnText}</ids-text>` : '';
    const label = opt.label || 'Filter Input';
    const placeholder = opt.placeholder ? ` placeholder="${opt.placeholder}"` : '';
    const disabled = opt.disabled ? ' disabled' : '';
    const readonly = opt.readonly ? ' readonly' : '';

    let value = this.#conditions.filter((c: any) => c.columnId === column.id)[0]?.value ?? null;
    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].input) this.#initial[column.id].input = { value };
    value = value ? ` value="${value}"` : '';

    return `<ids-trigger-field
      color-variant="${!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'}"
      data-filter-type="${type}"
      type="${opt.type || 'text'}"
      size="${opt.size || 'full'}"
      label="${label}"
      label-state="collapsed"
      id="${id}"
      no-margins
      compact
      ${placeholder}${disabled}${readonly}${value}>
      <ids-trigger-button id="${triggerBtnId}" slot="trigger-end"${triggerBtnIcon}>
        <ids-icon part="icon"></ids-icon>
        ${triggerBtnText}
      </ids-trigger-button>
    </ids-trigger-field>`;
  }

  /**
   * Get dropdown template string
   * @private
   * @param {string} type Must be exactly the same as filter method name
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The template string
   */
  #dropdownTemplate(type: any, column: any) {
    const id = `${this.#id(column)}-${type}`;
    const operators = this.#operators(type, column);

    const opt = column.filterOptions || {};
    const label = opt.label || 'Filter Dropdown';
    const disabled = opt.disabled ? ' disabled' : '';
    const readonly = opt.readonly ? ' readonly' : '';

    let sel: any = null;
    if (this.#conditions.length) {
      const condition = this.#conditions.filter((c: any) => c.columnId === column.id)[0];
      sel = operators.filter((op: any) => (op.label === condition?.value))[0];
    }
    if (!sel) sel = operators.filter((op: any) => op.selected)[0];
    let items = '';
    const { value: nfValue, label: nfLabel } = this.#dropdownNotFilterItem(column);
    const notFilteredItemTemplate = () => {
      if (!sel) sel = { value: nfValue };
      items = `<ids-list-box-option id="${id}-opt-not-filtered" value="${nfValue}">${nfLabel}</ids-list-box-option>`;
    };

    if (!operators.length) {
      notFilteredItemTemplate();
    } else {
      const found = operators.some((op: any) => op.value === nfValue);
      if (!found) notFilteredItemTemplate();
      operators.forEach((op: any, i: any) => {
        items += `<ids-list-box-option id="${id}-opt-${i}" value="${op.value}">${op.label}</ids-list-box-option>`;
      });
    }
    if (!sel) sel = operators[0];
    const value = (sel?.value || sel?.value === '') ? ` value="${sel.value}"` : '';
    const isUppercase = (): boolean => {
      if (typeof column?.uppercase === 'function') return column.uppercase('header-cell', column);
      return (column?.uppercase === 'true' || column?.uppercase === true);
    };
    const uppercaseClass = isUppercase() ? ' uppercase' : '';

    this.#initial[column.id] = this.#initial[column.id] || {};
    if (!this.#initial[column.id].dropdown) this.#initial[column.id].dropdown = { ...sel };

    return `
      <ids-dropdown
        color-variant="${!this.root.listStyle ? 'alternate-formatter' : 'alternate-list-formatter'}"
        label="${label}"
        label-state="collapsed"
        list="#${id}-list"
        no-margins
        compact
        data-filter-type="${type}"
        size="${opt.size || 'full'}"
        id="${id}"
        ${value}${disabled}${readonly}
      >
      </ids-dropdown>
      <ids-dropdown-list id="${id}-list">
        <ids-list-box${uppercaseClass}>${items}</ids-list-box>
      </ids-dropdown-list>
    `;
  }

  /**
   * Get list of filter wrapper elements
   * @returns {Array<HTMLElement>} List of filter wrapper elements
   */
  get filterNodes() {
    return this.root?.shadowRoot?.querySelectorAll('.ids-data-grid-header-cell-filter-wrapper');
  }

  /**
   * Get not filter item for dropdown
   * @private
   * @param {IdsDataGridColumn} column The column info
   * @returns {object} The item
   */
  #dropdownNotFilterItem(column: IdsDataGridColumn) {
    const item = (column?.filterOptions as any)?.notFilteredItem || {};
    return { value: item.value || 'not-filtered', label: item.label || '' };
  }

  /**
   * Get unique id string
   * @private
   * @param {IdsDataGridColumn} column The column info
   * @returns {string} The id string
   */
  #id(column?: IdsDataGridColumn) {
    const columnId = column?.id ? `${column.id}-` : '';
    return `ids-data-grid-filter-${columnId}${this.root.cacheHash}`;
  }

  /**
   * Get button operators by filter type
   * @private
   * @param {string} type The filter type
   * @param {IdsDataGridColumn} column The column info
   * @returns {Array} List of button operators
   */
  #operators(type: string, column: IdsDataGridColumn): IdsDataGridFilterOperator[] {
    /**
     * Transform string to camel case
     * @param {string} val the target text
     * @returns {string} the camel case text
     */
    function toCamelCase(val: string) {
      return val.replace(/^./, val[0].toUpperCase());
    }

    /**
     * Transform string to operator key
     * @param {string} val the target text
     * @returns {string} the operator key
     */
    function getOperatorKey(val: string) {
      const keys = val.split('-');
      let operator = '';
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex++) {
        if (keyIndex === 0) operator += `${keys[keyIndex]}`;
        else operator += `${toCamelCase(keys[keyIndex])}`;
      }

      return operator;
    }

    const ds = this.#operatorsDataset;

    // Localization
    for (const key in ds) {
      if (ds.hasOwnProperty(key)) {
        const val = toCamelCase(key);
        (ds as any)[key].label = this.root.localeAPI.translate(val);
      }
    }

    if (column?.filterConditions?.constructor === Array) {
      for (let i = 0; i < column.filterConditions.length; i++) {
        const opKey = getOperatorKey(column.filterConditions[i].value);
        if ((ds as any)[opKey]) column.filterConditions[i].label = (ds as any)[opKey].label;
      }

      return column.filterConditions;
    }

    let operators: any = [];

    // Collect and built, all the operators for contents type
    const contentsOperators = () => {
      const val = this.root.datasource.allData.map((row: any) => escapeHTML(row[column.id]));
      return [...new Set(val)].map((v) => ({ value: v || '', label: v || '' }));
    };

    switch (type) {
      case 'text':
        operators = [
          ds.contains,
          ds.doesNotContain,
          ds.equals,
          ds.doesNotEqual,
          ds.isEmpty,
          ds.isNotEmpty,
          ds.endsWith,
          ds.doesNotEndWith,
          ds.startsWith,
          ds.doesNotStartWith
        ];
        break;
      case 'decimal':
      case 'integer':
        operators = [
          ds.equals,
          ds.doesNotEqual,
          ds.isEmpty,
          ds.isNotEmpty,
          ds.lessThan,
          ds.lessOrEquals,
          ds.greaterThan,
          ds.greaterOrEquals
        ];
        break;
      case 'time':
        operators = [
          ds.equals,
          ds.doesNotEqual,
          ds.isEmpty,
          ds.isNotEmpty,
          ds.earlierThan,
          ds.earlierOrEquals,
          ds.laterThan,
          ds.laterOrEquals
        ];
        break;
      case 'checkbox':
        operators = [
          ds.all,
          ds.selected,
          ds.notSelected
        ];
        break;
      case 'contents':
        operators = contentsOperators();
        break;
      case 'date':
        operators = [
          ds.equals,
          ds.doesNotEqual,
          ds.isEmpty,
          ds.isNotEmpty,
          ds.inRange,
          ds.earlierThan,
          ds.earlierOrEquals,
          ds.laterThan,
          ds.laterOrEquals
        ];
        break;
      default:
        break;
    }

    return operators;
  }

  /**
   * Available operators for filter button.
   * @private
   * @type {object}
   */
  #operatorsDataset = {
    contains: { value: 'contains', label: 'Contains', icon: 'filter-contains' },
    doesNotContain: { value: 'does-not-contain', label: 'Does Not Contain', icon: 'filter-does-not-contain' },
    equals: { value: 'equals', label: 'Equals', icon: 'filter-equals' },
    doesNotEqual: { value: 'does-not-equal', label: 'Does Not Equals', icon: 'filter-does-not-equal' },
    isEmpty: { value: 'is-empty', label: 'Is Empty', icon: 'filter-is-empty' },
    isNotEmpty: { value: 'is-not-empty', label: 'Is Not Empty', icon: 'filter-is-not-empty' },
    endsWith: { value: 'end-with', label: 'Ends With', icon: 'filter-end-with' },
    doesNotEndWith: { value: 'does-not-end-with', label: 'Does Not End With', icon: 'filter-does-not-end-with' },
    startsWith: { value: 'start-with', label: 'Starts With', icon: 'filter-start-with' },
    doesNotStartWith: { value: 'does-not-start-with', label: 'Does Not Start With', icon: 'filter-does-not-start-with' },
    lessThan: { value: 'less-than', label: 'Less Than', icon: 'filter-less-than' },
    lessOrEquals: { value: 'less-equals', label: 'Less Or Equals', icon: 'filter-less-equals' },
    greaterThan: { value: 'greater-than', label: 'Greater Than', icon: 'filter-greater-than' },
    greaterOrEquals: { value: 'greater-equals', label: 'Greater Or Equals', icon: 'filter-greater-equals' },
    inRange: { value: 'in-range', label: 'In Range', icon: 'filter-in-range' },
    all: { value: 'selected-notselected', label: 'All', icon: 'filter-selected-notselected' },
    selected: { value: 'selected', label: 'Selected', icon: 'filter-selected' },
    notSelected: { value: 'not-selected', label: 'Not Selected', icon: 'filter-not-selected' },
    earlierThan: { value: 'earlier-than', label: 'Earlier than', icon: 'filter-less-than' },
    earlierOrEquals: { value: 'earlier-equals', label: 'Earlier than Or equals', icon: 'filter-less-equals' },
    laterThan: { value: 'later-than', label: 'Later than', icon: 'filter-greater-than' },
    laterOrEquals: { value: 'later-equals', label: 'Later than or equals', icon: 'filter-greater-equals' }
  };
}
